import { ProductPackagingLevelDTO } from './b2b-product-settings.dto'
import { Product } from './product.dto'

/**
 * Balení a palety z karty materiálu ve Visionu (pole z Elasticsearch / VisionAPI):
 *   obal + zmj_v_jednom_obalu -> první obal a počet základních MJ (mj_id) v něm
 *   obal1ks / obal2ks / obal3ks -> kolik obalů dané úrovně je v obalu o úroveň výš
 * Balení = úroveň "bal" větší než prodejní jednotka, paleta = "pal"/"pal-free".
 * Stejná pravidla používá VisionAPI (product-packaging.logic.ts) a FinSoft-B2B (lib/utils/packaging.ts).
 */
export const PACKAGING_PACKAGE_CODES: readonly string[] = ['bal']
export const PACKAGING_PALLET_CODES: readonly string[] = ['pal', 'pal-free']

type PackagingSource = Pick<
  Product,
  'koef_zvj_1' | 'obal' | 'zmj_v_jednom_obalu' | 'obal1ks' | 'obal2' | 'obal2ks' | 'obal3' | 'obal3ks' | 'obal4'
>

const EPSILON = 1e-6
const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '')
const numeric = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.trim()) : NaN
  return Number.isFinite(parsed) ? parsed : 0
}
const round4 = (value: number) => Math.round(value * 10_000) / 10_000
const isWholeMultiple = (value: number, base: number) => base > 0 && Math.abs(value / base - Math.round(value / base)) < EPSILON

export const readProductPackagingLevels = (product?: Partial<PackagingSource> | null): ProductPackagingLevelDTO[] => {
  const firstCode = text(product?.obal)
  const firstQuantity = numeric(product?.zmj_v_jednom_obalu)
  if (!product || !firstCode || firstQuantity <= 0) return []

  const levels: ProductPackagingLevelDTO[] = [{ code: firstCode, quantity: round4(firstQuantity), ean: null }]
  for (const level of [
    { code: product.obal2, count: product.obal1ks },
    { code: product.obal3, count: product.obal2ks },
    { code: product.obal4, count: product.obal3ks },
  ]) {
    const code = text(level.code)
    const count = numeric(level.count)
    if (!code || count <= 0) break
    levels.push({ code, quantity: round4(levels[levels.length - 1].quantity * count), ean: null })
  }
  return levels
}

/** Opak readProductPackagingLevels: úrovně z Visionu (VisionAPI /product-packaging) zpět na pole produktu jako v Elasticu. */
export const productPackagingFieldsFromLevels = (levels: Pick<ProductPackagingLevelDTO, 'code' | 'quantity'>[]) => {
  const count = (index: number) => {
    const lower = levels[index]
    const higher = levels[index + 1]
    return lower && higher && lower.quantity > 0 ? Math.round(higher.quantity / lower.quantity) : 0
  }
  return {
    obal: levels[0]?.code,
    zmj_v_jednom_obalu: levels[0]?.quantity ?? 0,
    obal1ks: count(0),
    obal2: levels[1]?.code,
    obal2ks: count(1),
    obal3: levels[2]?.code,
    obal3ks: count(2),
    obal4: levels[3]?.code,
  } satisfies Partial<PackagingSource>
}

/** Balení a paleta v základní MJ; null, pokud je produkt nemá nebo nenavazují na prodejní jednotku. */
export const describeProductPackaging = (product?: Partial<PackagingSource> | null) => {
  const koef = numeric(product?.koef_zvj_1)
  const step = koef > 0 ? koef : 1
  const levels = readProductPackagingLevels(product)
  const usable = (quantity: number, above: number) => quantity > above + EPSILON && isWholeMultiple(quantity, step)
  const packageLevel =
    levels.find(level => PACKAGING_PACKAGE_CODES.includes(level.code.toLowerCase()) && usable(level.quantity, step)) ?? null
  const palletLevel =
    levels.find(
      level => PACKAGING_PALLET_CODES.includes(level.code.toLowerCase()) && usable(level.quantity, packageLevel?.quantity ?? step),
    ) ?? null
  return { step, levels, package: packageLevel, pallet: palletLevel }
}
